import React, { Suspense, useMemo } from 'react';
import { Navigate, Outlet, useRoutes, type RouteObject } from 'react-router-dom';
import { publicRoutes, protectedCoreRoutes, fallbackRoutes } from './coreRoutes';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { useEnabledPlugins } from '../plugin/useEnabledPlugins';
import { useDisabledCoreItems } from '../plugin/useCoreItems';
import { isPathDisabled } from '../plugin/coreItems';
import { AuthGuard, CHANGE_PASSWORD_PATH } from '../auth/AuthGuard';
import { AppShell } from '../layout/AppShell';
import { ErrorBoundary } from '../components/ErrorBoundary';
import type { LazyRouteConfig } from '../plugin/types';

/**
 * Cache per i componenti lazy — evita che React.lazy() venga chiamato
 * ad ogni render creando un nuovo componente (che causa unmount/remount).
 */
const lazyComponentCache = new Map<string, React.LazyExoticComponent<React.ComponentType>>();

function getLazyComponent(path: string, lazyFn: () => Promise<{ default: React.ComponentType }>) {
  if (!lazyComponentCache.has(path)) {
    lazyComponentCache.set(path, React.lazy(lazyFn));
  }
  return lazyComponentCache.get(path)!;
}

function convertLazyRoutes(lazyRoutes: LazyRouteConfig[]): RouteObject[] {
  return lazyRoutes.map((route) => {
    const LazyComponent = getLazyComponent(route.path, route.lazy);
    const routeObject: RouteObject = {
      path: route.path,
      element: (
        <ErrorBoundary compact>
          <Suspense fallback={<div style={{ padding: '1rem' }}>Caricamento...</div>}>
            <LazyComponent />
          </Suspense>
        </ErrorBoundary>
      ),
    };
    if (route.children) {
      routeObject.children = convertLazyRoutes(route.children);
    }
    return routeObject;
  });
}

function AppRouterInner() {
  const enabledPlugins = useEnabledPlugins();
  const disabledCoreItems = useDisabledCoreItems();

  // Le rotte delle voci spente dalla pagina «Voci del CRM» (FILO-53) restano, ma portano alla
  // home: chi apre un vecchio link non vede la pagina e nemmeno un 404.
  const coreRoutes = useMemo(
    () =>
      disabledCoreItems.size
        ? protectedCoreRoutes.map((route) =>
            route.path && isPathDisabled(route.path, disabledCoreItems)
              ? { ...route, element: <Navigate to="/" replace /> }
              : route,
          )
        : protectedCoreRoutes,
    [disabledCoreItems],
  );

  const pluginRoutes = useMemo(() => {
    const lazyRoutes = enabledPlugins.flatMap((p) => p.routes ?? []);
    return convertLazyRoutes(lazyRoutes);
  }, [enabledPlugins]);

  const routes: RouteObject[] = useMemo(() => [
    ...publicRoutes,
    // Cambio password obbligatorio (TS-119): protetto — la chiamata di cambio
    // è autenticata e serve il token — ma FUORI dall'AppShell, così la pagina
    // non mostra sidebar e topbar di un CRM che l'utente non può ancora usare.
    // È l'AuthGuard stesso a dirottare qui ogni rotta protetta finché il flag
    // passwordReset è alzato.
    {
      element: (
        <AuthGuard>
          <Outlet />
        </AuthGuard>
      ),
      children: [{ path: CHANGE_PASSWORD_PATH, element: <ChangePasswordPage /> }],
    },
    {
      element: (
        <AuthGuard>
          <AppShell />
        </AuthGuard>
      ),
      children: [
        ...coreRoutes,
        ...pluginRoutes,
        // Il catch-all 404 sta DENTRO l'AppShell (guscio con TopBar+Sidebar):
        // così la NotFoundPage è resa nel layout applicativo, non su pagina
        // bianca. Essendo sotto AuthGuard, un utente non autenticato che apre
        // un url errato viene mandato al login invece di vedere un 404 nudo.
        ...fallbackRoutes,
      ],
    },
  ], [pluginRoutes, coreRoutes]);

  return useRoutes(routes);
}

export function AppRouter() {
  return <AppRouterInner />;
}
